// Secure Supabase client - only for authenticated operations through Edge Functions
// This prevents API keys from being exposed in the client bundle

import type { Database } from '../types/supabase';
import { createClient } from '@supabase/supabase-js';

// Direct Supabase client for authentication only
const supabaseUrl = 'https://fggiylzoktmnjfslrpyw.supabase.co';
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY || '';
const directSupabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    // Enable automatic session persistence
    persistSession: true,
    // Auto refresh tokens
    autoRefreshToken: true,
    // Detect session in URL (for magic links, etc.)
    detectSessionInUrl: true,
    // Store session in localStorage
    storage: typeof window !== 'undefined' ? window.localStorage : undefined,
  },
});

// Export directSupabase for direct database operations
export { directSupabase };

// Helper function to handle API errors
export const handleApiError = (error: any) => {
  console.error('API error:', error);
  throw new Error(error.message || 'An error occurred while interacting with the database');
};

// Direct authentication using Supabase client
export const directAuth = {
  signIn: async (email: string, password: string) => {
    const { data, error } = await directSupabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      throw new Error(error.message);
    }

    // Store access token in localStorage for Edge Function compatibility
    if (data.session?.access_token) {
      localStorage.setItem('sb-access-token', data.session.access_token);
    }

    return data;
  },

  signOut: async () => {
    // Clear any manually stored tokens for backward compatibility
    localStorage.removeItem('sb-access-token');
    const { error } = await directSupabase.auth.signOut();
    
    if (error) {
      throw new Error(error.message);
    }

    return { message: 'Successfully signed out' };
  },

  getSession: async () => {
    const { data: { session }, error } = await directSupabase.auth.getSession();
    
    if (error) {
      console.log('Session error:', error);
      return null;
    }

    // Sync localStorage token with Supabase session for Edge Function compatibility
    if (session?.access_token) {
      localStorage.setItem('sb-access-token', session.access_token);
    } else {
      localStorage.removeItem('sb-access-token');
    }

    return { session };
  }
};

// Secure API calls through Edge Functions (for external integrations)
export const secureApi = {
  // Authentication - migrated to directAuth
  signIn: async (email: string, password: string) => {
    return directAuth.signIn(email, password);
  },

  signOut: async () => {
    return directAuth.signOut();
  },

  getSession: async () => {
    return directAuth.getSession();
  },

  // Helper to add Authorization header if token exists
  _authHeaders: () => {
    const headers: Record<string, string> = {};
    const token = localStorage.getItem('sb-access-token');
    if (token) headers['Authorization'] = `Bearer ${token}`;
    return headers;
  },

  /** Live Jarvis session token. The stored copy goes stale after refresh. */
  _freshAuthHeaders: async () => {
    try {
      const { data: { session } } = await directSupabase.auth.getSession();
      if (session?.access_token) {
        localStorage.setItem('sb-access-token', session.access_token);
      }
    } catch {
      // keep whatever is already stored
    }
    return secureApi._authHeaders();
  },

  // Customer operations - KEEP as Edge Functions (external integration)
  getCustomers: async () => {
    const headers: Record<string, string> = {};
    const token = localStorage.getItem('sb-access-token');
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const response = await fetch('https://fggiylzoktmnjfslrpyw.supabase.co/functions/v1/client-customers', {
      headers
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to fetch customers');
    }
    
    return response.json();
  },

  createCustomer: async (customer: any) => {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    const token = localStorage.getItem('sb-access-token');
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const response = await fetch('https://fggiylzoktmnjfslrpyw.supabase.co/functions/v1/client-customers', {
      method: 'POST',
      headers,
      body: JSON.stringify(customer)
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to create customer');
    }
    
    return response.json();
  },

  updateCustomer: async (id: string, customer: any) => {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    const token = localStorage.getItem('sb-access-token');
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const response = await fetch(`https://fggiylzoktmnjfslrpyw.supabase.co/functions/v1/client-customers/${id}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(customer)
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to update customer');
    }
    
    return response.json();
  },

  deleteCustomer: async (id: string) => {
    const headers: Record<string, string> = {};
    const token = localStorage.getItem('sb-access-token');
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const response = await fetch(`https://fggiylzoktmnjfslrpyw.supabase.co/functions/v1/client-customers/${id}`, {
      method: 'DELETE',
      headers
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to delete customer');
    }
    
    return response.json();
  },

  // Properties operations - MIGRATED to direct Supabase
  getProperties: async () => {
    const { data, error } = await directSupabase
      .from('properties')
      .select(`
        *,
        property_customer_relationships (*, property_customer:customer_id (*))
      `)
      .order('created_at', { ascending: false });
    
    if (error) {
      throw new Error(error.message || 'Failed to fetch properties');
    }
    
    // Process the data to add backward compatibility
    const processedData = (data || []).map((property: any) => {
      const primaryContact = property.property_customer_relationships?.find((r: any) => r.is_primary_contact)?.property_customer;
      return {
        ...property,
        property_customer: primaryContact
      };
    });
    
    return { success: true, data: processedData };
  },

  getProperty: async (propertyId: string) => {
    const { data, error } = await directSupabase
      .from('properties')
      .select(`
        *,
        property_customer_relationships (*, property_customer:customer_id (*))
      `)
      .eq('id', propertyId)
      .single();
    
    if (error) {
      throw new Error(error.message || 'Failed to fetch property');
    }
    
    if (!data) {
      return { success: true, data: null };
    }
    
    // Process the data to add backward compatibility
    const primaryContact = data.property_customer_relationships?.find((r: any) => r.is_primary_contact)?.property_customer;
    const processedData = {
      ...data,
      property_customer: primaryContact
    };
    
    return { success: true, data: processedData };
  },

  createProperty: async (property: any) => {
    const { data, error } = await directSupabase
      .from('properties')
      .insert(property)
      .select()
      .single();
    
    if (error) {
      throw new Error(error.message || 'Failed to create property');
    }
    
    return { success: true, data };
  },

  updateProperty: async (propertyId: string, property: any) => {
    const { data, error } = await directSupabase
      .from('properties')
      .update(property)
      .eq('id', propertyId)
      .select()
      .single();
    
    if (error) {
      throw new Error(error.message || 'Failed to update property');
    }
    
    return { success: true, data };
  },

  deleteProperty: async (propertyId: string) => {
    const { error } = await directSupabase
      .from('properties')
      .delete()
      .eq('id', propertyId);
    
    if (error) {
      throw new Error(error.message || 'Failed to delete property');
    }
    
    return { success: true };
  },

  // Property Customer operations - MIGRATED to direct Supabase
  getPropertyCustomers: async () => {
    const { data, error } = await directSupabase
      .from('property_customer')
      .select(`
        *,
        property_customer_relationships (*, properties:property_id (*))
      `)
      .order('created_at', { ascending: false });
    
    if (error) {
      throw new Error(error.message || 'Failed to fetch property customers');
    }
    
    // Process the data to add backward compatibility
    const processedData = (data || []).map((customer: any) => {
      const properties = customer.property_customer_relationships?.map((r: any) => r.properties) || [];
      return {
        ...customer,
        properties: properties
      };
    });
    
    return { success: true, data: processedData };
  },

  getPropertyCustomer: async (customerId: string) => {
    const { data, error } = await directSupabase
      .from('property_customer')
      .select(`
        *,
        property_customer_relationships (*, properties:property_id (*))
      `)
      .eq('id', customerId)
      .single();
    
    if (error) {
      throw new Error(error.message || 'Failed to fetch property customer');
    }
    
    if (!data) {
      return { success: true, data: null };
    }
    
    // Process the data to add backward compatibility
    const properties = data.property_customer_relationships?.map((r: any) => r.properties) || [];
    const processedData = {
      ...data,
      properties: properties
    };
    
    return { success: true, data: processedData };
  },

  createPropertyCustomer: async (customer: any) => {
    const { data, error } = await directSupabase
      .from('property_customer')
      .insert(customer)
      .select()
      .single();
    
    if (error) {
      throw new Error(error.message || 'Failed to create property customer');
    }
    
    return { success: true, data };
  },

  updatePropertyCustomer: async (customerId: string, customer: any) => {
    const { data, error } = await directSupabase
      .from('property_customer')
      .update(customer)
      .eq('id', customerId)
      .select()
      .single();
    
    if (error) {
      throw new Error(error.message || 'Failed to update property customer');
    }
    
    return { success: true, data };
  },

  deletePropertyCustomer: async (customerId: string) => {
    const { error } = await directSupabase
      .from('property_customer')
      .delete()
      .eq('id', customerId);
    
    if (error) {
      throw new Error(error.message || 'Failed to delete property customer');
    }
    
    return { success: true };
  },

  // Profile operations - MIGRATED to direct Supabase
  getProfile: async (userId: string) => {
    const { data, error } = await directSupabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single();
    
    if (error) {
      throw new Error(error.message || 'Failed to fetch profile');
    }
    
    return { success: true, data };
  },

  getProfileByEmail: async (email: string) => {
    const { data, error } = await directSupabase
      .from('profiles')
      .select('*')
      .eq('email', email)
      .single();
    
    if (error) {
      throw new Error(error.message || 'Failed to fetch profile by email');
    }
    
    return { success: true, data };
  },

  createProfile: async (profile: any) => {
    const { data, error } = await directSupabase
      .from('profiles')
      .insert(profile)
      .select()
      .single();
    
    if (error) {
      throw new Error(error.message || 'Failed to create profile');
    }
    
    return { success: true, data };
  },

  updateProfile: async (userId: string, profile: any) => {
    const { data, error } = await directSupabase
      .from('profiles')
      .update(profile)
      .eq('id', userId)
      .select()
      .single();
    
    if (error) {
      throw new Error(error.message || 'Failed to update profile');
    }
    
    return { success: true, data };
  },

  // Project operations - KEEP as Edge Functions (external integration)
  getProjects: async () => {
    const headers = await secureApi._freshAuthHeaders();
    const response = await fetch('https://fggiylzoktmnjfslrpyw.supabase.co/functions/v1/client-projects', {
      headers
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to fetch projects');
    }
    
    return response.json();
  },

  getProject: async (projectId: string) => {
    const headers = await secureApi._freshAuthHeaders();
    const response = await fetch(`https://fggiylzoktmnjfslrpyw.supabase.co/functions/v1/client-projects/${projectId}`, {
      headers
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to fetch project');
    }
    
    return response.json();
  },

  createProject: async (project: any) => {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    const token = localStorage.getItem('sb-access-token');
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const response = await fetch('https://fggiylzoktmnjfslrpyw.supabase.co/functions/v1/client-projects', {
      method: 'POST',
      headers,
      body: JSON.stringify(project)
    });
    const result = await response.json();
    if (!response.ok || !result.success) {
      throw new Error(result.error || 'Failed to create project');
    }
    return result.data;
  },

  updateProject: async (id: string, project: any) => {
    const response = await fetch(`https://fggiylzoktmnjfslrpyw.supabase.co/functions/v1/client-projects/${id}`, {
      method: 'PUT',
      headers: { ...secureApi._authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(project)
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to update project');
    }
    
    return response.json();
  },

  deleteProject: async (id: string) => {
    const response = await fetch(`https://fggiylzoktmnjfslrpyw.supabase.co/functions/v1/client-projects/${id}`, {
      method: 'DELETE',
      headers: { ...secureApi._authHeaders() }
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to delete project');
    }
    
    return response.json();
  },

  // Project events operations - KEEP as Edge Functions (external integration)
  getProjectEvents: async (projectId: string) => {
    const response = await fetch(`https://fggiylzoktmnjfslrpyw.supabase.co/functions/v1/client-projects/events?projectId=${projectId}`, {
      headers: { ...secureApi._authHeaders() }
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to fetch project events');
    }
    
    const result = await response.json();
    return result.data;
  },

  createProjectEvent: async (event: any) => {
    const response = await fetch('https://fggiylzoktmnjfslrpyw.supabase.co/functions/v1/client-projects/events', {
      method: 'POST',
      headers: { ...secureApi._authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(event)
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to create project event');
    }
    
    return response.json();
  },

  updateProjectEvent: async (eventId: string, event: any) => {
    const response = await fetch(`https://fggiylzoktmnjfslrpyw.supabase.co/functions/v1/client-projects/events?eventId=${eventId}`, {
      method: 'PUT',
      headers: { ...secureApi._authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(event)
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to update project event');
    }
    
    return response.json();
  },

  // Supplier operations - MIGRATED to direct Supabase
  getSuppliers: async () => {
    const { data, error } = await directSupabase
      .from('suppliers')
      .select('*')
      .order('supplier_name', { ascending: true });
    
    if (error) {
      throw new Error(error.message || 'Failed to fetch suppliers');
    }
    
    return { success: true, data };
  },

  createSupplier: async (supplier: any) => {
    const { data, error } = await directSupabase
      .from('suppliers')
      .insert(supplier)
      .select()
      .single();
    
    if (error) {
      throw new Error(error.message || 'Failed to create supplier');
    }
    
    return { success: true, data };
  },

  updateSupplier: async (id: string, supplier: any) => {
    const { data, error } = await directSupabase
      .from('suppliers')
      .update(supplier)
      .eq('id', id)
      .select()
      .single();
    
    if (error) {
      throw new Error(error.message || 'Failed to update supplier');
    }
    
    return { success: true, data };
  },

  deleteSupplier: async (id: string) => {
    const { error } = await directSupabase
      .from('suppliers')
      .delete()
      .eq('id', id);
    
    if (error) {
      throw new Error(error.message || 'Failed to delete supplier');
    }
    
    return { success: true };
  },

  // Pricing operations - MIGRATED to direct Supabase
  getSupplierPrices: async (supplierId: string) => {
    const { data, error } = await directSupabase
      .from('supplier_prices')
      .select('*')
      .eq('supplier_id', supplierId)
      .order('glass_type', { ascending: true });
    
    if (error) {
      throw new Error(error.message || 'Failed to fetch supplier prices');
    }
    
    return { success: true, data };
  },

  getGlassExtras: async () => {
    const { data, error } = await directSupabase
      .from('glass_extras')
      .select('*')
      .order('name', { ascending: true });
    
    if (error) {
      throw new Error(error.message || 'Failed to fetch glass extras');
    }
    
    return { success: true, data };
  },

  getSupplierExtras: async (supplierId: string) => {
    const { data, error } = await directSupabase
      .from('supplier_extras')
      .select('*, glass_extras(name)')
      .eq('supplier_id', supplierId);
    
    if (error) {
      throw new Error(error.message || 'Failed to fetch supplier extras');
    }
    
    return { success: true, data };
  },

  // Supplier Price operations - MIGRATED to direct Supabase
  createSupplierPrice: async (price: any) => {
    const { data, error } = await directSupabase
      .from('supplier_prices')
      .insert(price)
      .select()
      .single();
    
    if (error) {
      throw new Error(error.message || 'Failed to create supplier price');
    }
    
    return { success: true, data };
  },

  updateSupplierPrice: async (id: string, price: any) => {
    const { data, error } = await directSupabase
      .from('supplier_prices')
      .update(price)
      .eq('id', id)
      .select()
      .single();
    
    if (error) {
      throw new Error(error.message || 'Failed to update supplier price');
    }
    
    return { success: true, data };
  },

  deleteSupplierPrice: async (id: string) => {
    const { error } = await directSupabase
      .from('supplier_prices')
      .delete()
      .eq('id', id);
    
    if (error) {
      throw new Error(error.message || 'Failed to delete supplier price');
    }
    
    return { success: true };
  },

  // Glass Extra operations - MIGRATED to direct Supabase
  createGlassExtra: async (extra: any) => {
    const { data, error } = await directSupabase
      .from('glass_extras')
      .insert(extra)
      .select()
      .single();
    
    if (error) {
      throw new Error(error.message || 'Failed to create glass extra');
    }
    
    return { success: true, data };
  },

  updateGlassExtra: async (id: string, extra: any) => {
    const { data, error } = await directSupabase
      .from('glass_extras')
      .update(extra)
      .eq('id', id)
      .select()
      .single();
    
    if (error) {
      throw new Error(error.message || 'Failed to update glass extra');
    }
    
    return { success: true, data };
  },

  deleteGlassExtra: async (id: string) => {
    const { error } = await directSupabase
      .from('glass_extras')
      .delete()
      .eq('id', id);
    
    if (error) {
      throw new Error(error.message || 'Failed to delete glass extra');
    }
    
    return { success: true };
  },

  // Supplier Extra operations - MIGRATED to direct Supabase
  createSupplierExtra: async (extra: any) => {
    const { data, error } = await directSupabase
      .from('supplier_extras')
      .insert(extra)
      .select()
      .single();
    
    if (error) {
      throw new Error(error.message || 'Failed to create supplier extra');
    }
    
    return { success: true, data };
  },

  updateSupplierExtra: async (id: string, price: number) => {
    const { data, error } = await directSupabase
      .from('supplier_extras')
      .update({ price })
      .eq('id', id)
      .select()
      .single();
    
    if (error) {
      throw new Error(error.message || 'Failed to update supplier extra');
    }
    
    return { success: true, data };
  },

  deleteSupplierExtra: async (id: string) => {
    const { error } = await directSupabase
      .from('supplier_extras')
      .delete()
      .eq('id', id);
    
    if (error) {
      throw new Error(error.message || 'Failed to delete supplier extra');
    }
    
    return { success: true };
  },

  // Email Activity operations - MIGRATED to direct Supabase
  getEmailActivity: async (page: number = 1, pageSize: number = 20, filters?: any) => {
    let query = directSupabase
      .from('email_activity')
      .select('*')
      .order('created_at', { ascending: false })
      .range((page - 1) * pageSize, page * pageSize - 1);
    
    if (filters) {
      // Apply filters if provided
      if (filters.email) {
        query = query.eq('email', filters.email);
      }
      if (filters.event_type) {
        query = query.eq('event_type', filters.event_type);
      }
    }
    
    const { data, error } = await query;
    
    if (error) {
      throw new Error(error.message || 'Failed to fetch email activity');
    }
    
    return { success: true, data };
  },

  getEmailActivityStats: async () => {
    const { data, error } = await directSupabase
      .from('email_activity')
      .select('event_type, created_at');
    
    if (error) {
      throw new Error(error.message || 'Failed to fetch email activity stats');
    }
    
    // Process stats from raw data
    const stats = {
      total: data?.length || 0,
      byType: {} as Record<string, number>,
      byDate: {} as Record<string, number>
    };
    
    data?.forEach(activity => {
      // Count by event type
      stats.byType[activity.event_type] = (stats.byType[activity.event_type] || 0) + 1;
      
      // Count by date
      const date = new Date(activity.created_at).toISOString().split('T')[0];
      stats.byDate[date] = (stats.byDate[date] || 0) + 1;
    });
    
    return { success: true, data: stats };
  },

  logEmailActivity: async (log: any) => {
    const { data, error } = await directSupabase
      .from('email_activity')
      .insert(log)
      .select()
      .single();
    
    if (error) {
      throw new Error(error.message || 'Failed to log email activity');
    }
    
    return { success: true, data };
  },

  // Email Template operations - MIGRATED to direct Supabase
  getEmailTemplates: async () => {
    const { data, error } = await directSupabase
      .from('email_templates')
      .select('*')
      .order('template_type', { ascending: true });
    
    if (error) {
      throw new Error(error.message || 'Failed to fetch email templates');
    }
    
    return { success: true, data };
  },

  getEmailTemplateByType: async (templateType: string) => {
    const { data, error } = await directSupabase
      .from('email_templates')
      .select('*')
      .eq('template_type', templateType)
      .single();
    
    if (error) {
      throw new Error(error.message || 'Failed to fetch email template');
    }
    
    return { success: true, data };
  },

  getEmailTemplateVersions: async (templateType: string) => {
    const { data, error } = await directSupabase
      .from('email_templates')
      .select('*')
      .eq('template_type', templateType)
      .order('version', { ascending: false });
    
    if (error) {
      throw new Error(error.message || 'Failed to fetch email template versions');
    }
    
    return { success: true, data };
  },

  createEmailTemplate: async (template: any) => {
    const { data, error } = await directSupabase
      .from('email_templates')
      .insert(template)
      .select()
      .single();
    
    if (error) {
      throw new Error(error.message || 'Failed to create email template');
    }
    
    return { success: true, data };
  },

  restoreEmailTemplateDefault: async (templateType: string) => {
    console.log('secureApi.restoreEmailTemplateDefault: Calling restore endpoint with templateType:', templateType);
    const headers = { ...secureApi._authHeaders(), 'Content-Type': 'application/json' };
    console.log('secureApi.restoreEmailTemplateDefault: Headers being sent:', headers);
    const response = await fetch('https://fggiylzoktmnjfslrpyw.supabase.co/functions/v1/client-email-templates/restore-default', {
      method: 'POST',
      headers,
      body: JSON.stringify({ template_type: templateType })
    });
    
    console.log('secureApi.restoreEmailTemplateDefault: Response status:', response.status);
    
    if (!response.ok) {
      const error = await response.json();
      console.log('secureApi.restoreEmailTemplateDefault: Error response:', error);
      throw new Error(error.message || 'Failed to restore email template default');
    }
    
    const result = await response.json();
    console.log('secureApi.restoreEmailTemplateDefault: Success result:', result);
    return result;
  },

  saveEmailTemplate: async (template: any, asDefault: boolean = false) => {
    console.log('secureApi.saveEmailTemplate: Calling save endpoint');
    const response = await fetch('https://fggiylzoktmnjfslrpyw.supabase.co/functions/v1/client-email-templates/save', {
      method: 'POST',
      headers: { ...secureApi._authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ template, asDefault })
    });
    
    console.log('secureApi.saveEmailTemplate: Response status:', response.status);
    
    if (!response.ok) {
      const error = await response.json();
      console.log('secureApi.saveEmailTemplate: Error response:', error);
      throw new Error(error.message || 'Failed to save email template');
    }
    
    const result = await response.json();
    console.log('secureApi.saveEmailTemplate: Success result:', result);
    return result;
  },
};

// Legacy exports for backward compatibility (will be removed)
export const supabase = {
  auth: {
    signInWithPassword: async (credentials: { email: string; password: string }) => {
      return secureApi.signIn(credentials.email, credentials.password);
    },
    signOut: async () => {
      return secureApi.signOut();
    },
    getSession: async () => {
      return secureApi.getSession();
    }
  }
};

export const handleSupabaseError = handleApiError; 